import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle, Loader2, Plus, Trash2, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";
import AddressFields from "@/components/AddressFields";
import { formatPhone, normalizePhoneForSave, isAddressValid } from "@/lib/utils";
import { loadDietaryOptions } from "@/lib/dietary-options";
import { formatApiError } from "@/lib/api";
import { buildClientSetupPayload } from "@/lib/clientSetupFlow";

const RELATIONSHIPS = ["Child", "FosterChild", "Friend", "GrandChild", "GrandParent", "GreatGrandChild", "GreatGrandParent", "Parent", "Sibling", "Spouse"];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

function PortalField({ label, value, onChange, onBlur, testid, type = "text", placeholder, disabled = false }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] text-base"
        style={{
          borderColor: "var(--fb-border)",
          background: disabled ? "var(--fb-bg)" : "#FAFAF8",
          color: disabled ? "var(--fb-text-muted)" : "var(--fb-text)"
        }}
      />
    </div>
  );
}

export default function ClientSetup() {
  const { state } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeSetup, verifyMagicLink } = useAuth();
  const setupToken = params.get("token");
  const verifiedRef = useRef(false);
  
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    gender: "",
    birth_year: null,
    dietary_restrictions: [],
  });
  const [members, setMembers] = useState([]);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [verifiedEmail, setVerifiedEmail] = useState(state?.email || "");
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;
    if (!setupToken) {
      setError("This setup link is missing or malformed. Request a new link to continue.");
      setVerifying(false);
      return;
    }
    (async () => {
      try {
        const data = await verifyMagicLink(setupToken);
        if (data.purpose !== "setup") {
          navigate("/portal", { replace: true });
          return;
        }
        setVerifiedEmail(data.email);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setVerifying(false);
      }
    })();
    loadDietaryOptions().then(setDietaryOptions);
  }, [setupToken, verifyMagicLink, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--fb-bg)" }}>
        <div className="max-w-md w-full fb-card p-10 text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: "var(--fb-primary)" }} />
          <p style={{ color: "var(--fb-text-muted)" }}>Verifying your setup link…</p>
        </div>
      </div>
    );
  }

  if (!setupToken || !verifiedEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--fb-bg)" }}>
        <div className="max-w-md w-full fb-card p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--fb-error)" }} />
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Account setup unavailable</h1>
          <p className="text-sm mb-5" style={{ color: "var(--fb-text-muted)" }}>{error}</p>
          <button onClick={() => navigate("/portal/login")} className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold">
            Request a new link
          </button>
        </div>
      </div>
    );
  }

  async function submit() {
    setError("");
    setSaving(true);
    
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      setError("First and last name are required.");
      setSaving(false);
      return;
    }
    
    if (!isAddressValid(form.address)) {
      setError("Address is incomplete. Please fill in Street, City, State, and a 5-digit Zip.");
      setSaving(false);
      return;
    }
    
    try {
      const payload = buildClientSetupPayload({
        setupToken,
        form: { ...form, phone: normalizePhoneForSave(form.phone) },
        members,
      });
      
      await completeSetup(payload);
      navigate("/portal", { replace: true });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  function addMember() {
    setMembers([...members, { first_name: "", last_name: "", gender: "", birth_year: null, relationship: "Child" }]);
  }
  
  function removeMember(i) {
    setMembers(members.filter((_, j) => j !== i));
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--fb-bg)" }} data-testid="client-setup">
      <header className="bg-white border-b" style={{ borderColor: "var(--fb-border)" }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div>
              <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
              <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Client Portal Registration</div>
            </div>
          </div>
          <button onClick={() => navigate("/portal/login")}
                  className="text-sm font-medium hover:underline text-stone-500">
            Cancel
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6 page-enter">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Welcome to the Client Portal</h1>
          <p className="text-base sm:text-lg mt-2" style={{ color: "var(--fb-text-muted)" }}>
            Please set up your household profile to continue. This information helps us serve you better.
          </p>
        </div>

        {error && (
          <div className="fb-status-error p-4 rounded-xl text-sm" data-testid="setup-error">{error}</div>
        )}

        <div className="fb-card p-4 sm:p-6 space-y-4">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Your Information</h3>
          
          <div className="p-4 rounded-xl border border-dashed mb-4" style={{ backgroundColor: "var(--fb-bg)", borderColor: "var(--fb-border)" }}>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "#8B918B" }}>
              Verified Email
            </label>
            <div className="text-sm font-medium" data-testid="setup-verified-email">
              {verifiedEmail}
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
              This email is locked for your security. Staff use it to send portal access links.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PortalField label="First Name *" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="setup-firstname" />
            <PortalField label="Last Name *" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="setup-lastname" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Gender</label>
              <select
                data-testid="setup-gender"
                value={form.gender || ""}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] bg-white text-base"
                style={{ borderColor: "var(--fb-border)" }}
              >
                <option value="">— Select —</option>
                {GENDERS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Age</label>
              <input
                data-testid="setup-age"
                type="number" min="0" max="120"
                value={form.birth_year ? new Date().getFullYear() - form.birth_year : ""}
                onChange={(e) => {
                  const a = parseInt(e.target.value);
                  setForm({
                    ...form,
                    birth_year: isNaN(a) ? null : new Date().getFullYear() - a,
                  });
                }}
                placeholder="Age in years"
                className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] text-base tabular-nums"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>
          </div>
          
          <PortalField label="Phone" value={formatPhone(form.phone)} onChange={(v) => setForm({ ...form, phone: v })} onBlur={() => setForm((prev) => ({ ...prev, phone: normalizePhoneForSave(prev.phone) }))} testid="setup-phone" placeholder="(555) 555-5555" />
          
          <div className="pt-2">
            <AddressFields value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v, google_verification_token: "" }))} onVerification={(t) => setForm((f) => ({ ...f, google_verification_token: t || "" }))} />
          </div>

          <div className="pt-4 border-t" style={{ borderColor: "var(--fb-border)" }}>
            <label className="block text-sm font-medium mb-2">Dietary Restrictions</label>
            <div className="flex flex-wrap gap-2" data-testid="setup-dietary-restrictions">
              {[...new Set([...dietaryOptions, ...(form.dietary_restrictions || [])])].map((opt) => {
                const selected = (form.dietary_restrictions || []).includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const current = form.dietary_restrictions || [];
                      const next = selected ? current.filter((x) => x !== opt) : [...current, opt];
                      setForm({ ...form, dietary_restrictions: next });
                    }}
                    className="px-3 py-2 rounded-full text-sm font-medium border-2 transition-colors"
                    style={{
                      borderColor: selected ? "var(--fb-primary)" : "var(--fb-border)",
                      background: selected ? "var(--fb-primary)" : "white",
                      color: selected ? "white" : "var(--fb-text)",
                    }}
                    data-testid={`setup-diet-${opt}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--fb-text-muted)" }}>
              Select all that apply. These help staff choose appropriate food items for your household.
            </p>
          </div>
        </div>

        <div className="fb-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Household Members</h3>
              <p className="text-sm mt-1" style={{ color: "var(--fb-text-muted)" }}>Add anyone else who lives with you.</p>
            </div>
            <button data-testid="setup-add-member" onClick={addMember}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
                    style={{ borderColor: "var(--fb-border)" }}>
              <Plus className="w-4 h-4" /> Add member
            </button>
          </div>
          
          <div className="space-y-3">
            {members.length > 0 && (
              <div className="hidden md:grid grid-cols-12 gap-2 px-3 pb-1 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
                <div className="col-span-3">First Name</div>
                <div className="col-span-3">Last Name</div>
                <div className="col-span-2 text-center">Age</div>
                <div className="col-span-3">Relationship</div>
                <div className="col-span-1 text-right">Remove</div>
              </div>
            )}
            
            {members.map((m, i) => {
              const currentYear = new Date().getFullYear();
              const age = m.birth_year ? currentYear - m.birth_year : "";
              return (
                <div key={i} className="grid grid-cols-6 md:grid-cols-12 gap-2 items-center p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}>
                  <input value={m.first_name || ""} onChange={(e) => setMembers(members.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))}
                         placeholder="First name" aria-label="First name"
                         className="col-span-3 md:col-span-3 px-3 py-2.5 rounded-lg border outline-none focus:border-[#052F66] bg-white" style={{ borderColor: "var(--fb-border)" }} />
                  <input value={m.last_name || ""} onChange={(e) => setMembers(members.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))}
                         placeholder="Last name" aria-label="Last name"
                         className="col-span-3 md:col-span-3 px-3 py-2.5 rounded-lg border outline-none focus:border-[#052F66] bg-white" style={{ borderColor: "var(--fb-border)" }} />
                  <input type="number" min="0" max="120" value={age} aria-label="Age in years"
                         onChange={(e) => {
                           const a = parseInt(e.target.value);
                           setMembers(members.map((x, j) => j === i ? {
                             ...x,
                             birth_year: isNaN(a) ? null : currentYear - a,
                           } : x));
                         }}
                         placeholder="Age"
                         className="col-span-2 md:col-span-2 px-3 py-2.5 rounded-lg border tabular-nums text-center outline-none focus:border-[#052F66] bg-white" style={{ borderColor: "var(--fb-border)" }} />
                  <select value={m.relationship || ""} onChange={(e) => setMembers(members.map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))}
                          aria-label="Relationship"
                          className="col-span-3 md:col-span-3 px-3 py-2.5 rounded-lg border bg-white outline-none focus:border-[#052F66]" style={{ borderColor: "var(--fb-border)" }}>
                    <option value="">Relationship</option>
                    {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <button onClick={() => removeMember(i)} aria-label="Remove member"
                          className="col-span-1 md:col-span-1 p-2 rounded-lg hover:bg-rose-50 flex justify-center transition-colors">
                    <Trash2 className="w-5 h-5" style={{ color: "var(--fb-error)" }} />
                  </button>
                </div>
              );
            })}
            
            {members.length === 0 && (
              <div className="text-center py-8 rounded-xl border border-dashed" style={{ borderColor: "var(--fb-border)" }}>
                <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Just you for now. Add family members if they live with you.</p>
              </div>
            )}
          </div>
        </div>

        <button data-testid="setup-submit-btn" onClick={submit} disabled={saving}
                className="w-full fb-btn-primary py-4 rounded-xl text-lg font-semibold inline-flex items-center justify-center gap-2 transition-transform active:scale-[0.98]">
          {saving ? "Completing setup..." : "Complete Setup"}
          {!saving && <ChevronRight className="w-5 h-5" />}
        </button>
      </main>
    </div>
  );
}
